import { z } from 'zod';
import {
    parseJsonWithNativeSafeNumbers,
    stringifyLosslessJson,
} from '../../lossless-json-codec';
import { CardPointeCardSecureTokenizationSuccessResponseSchema } from '../../schemas/ipg/cardpointe/cardpointe-tokenization.schemas';
import {
    CardPointeTokenizationResultType,
    type CardPointeCardSecureBrowserTokenizationResult,
} from '../../types/ipg/cardpointe/cardpointe-tokenization.types';

export type CardPointeBrowserTokenizationFetch = (
    input: RequestInfo | URL,
    init?: RequestInit
) => Promise<Response>;

export interface CardPointeBrowserTokenizationInput {
    readonly cardData: { readonly cardNumber: string };
    readonly cardSecureBaseUrl: string;
    readonly fetchImplementation?: CardPointeBrowserTokenizationFetch;
}

export interface CardPointeCardSecureTokenizationRequest {
    readonly url: string;
    readonly init: RequestInit;
}

interface CardPointeCardSecureFailureResponse {
    readonly message: string;
    readonly errorcode: number;
}

const CardPointeCardSecureFailureResponseSchema: z.ZodType<CardPointeCardSecureFailureResponse> =
    z
        .object({
            message: z.string().min(1),
            errorcode: z.number().int().refine((errorCode) => errorCode !== 0),
        })
        .strict();

export function buildCardPointeCardSecureTokenizationRequest(input: {
    readonly cardSecureBaseUrl: string;
    readonly normalizedPan: string;
}): CardPointeCardSecureTokenizationRequest {
    if (input.normalizedPan.length === 0) {
        throw new Error('CardPointe card tokenization requires a card number.');
    }
    const parsedBaseUrl = new URL(input.cardSecureBaseUrl);
    parsedBaseUrl.pathname = parsedBaseUrl.pathname.replace(/\/+$/, '');
    parsedBaseUrl.search = '';
    parsedBaseUrl.hash = '';
    const baseUrl = parsedBaseUrl.toString().replace(/\/+$/, '');
    return {
        url: `${baseUrl}/cardsecure/api/v1/ccn/tokenize`,
        init: {
            method: 'POST',
            headers: {
                accept: 'application/json',
                'content-type': 'application/json',
            },
            body: stringifyLosslessJson({ account: input.normalizedPan }),
        },
    };
}

export async function tokenizeCardWithCardPointeCardSecure(
    input: CardPointeBrowserTokenizationInput
): Promise<CardPointeCardSecureBrowserTokenizationResult> {
    const request = buildCardPointeCardSecureTokenizationRequest({
        cardSecureBaseUrl: input.cardSecureBaseUrl,
        normalizedPan: input.cardData.cardNumber.replace(/\D/g, ''),
    });
    const response = await (input.fetchImplementation ?? fetch)(request.url, request.init);
    let responseBody: unknown;
    try {
        responseBody = parseJsonWithNativeSafeNumbers(await response.text());
    } catch {
        throw new Error('CardPointe card tokenization response was not valid JSON.');
    }
    if (!response.ok) {
        throw new Error(`CardPointe card tokenization failed with HTTP ${response.status}.`);
    }
    const success = CardPointeCardSecureTokenizationSuccessResponseSchema.safeParse(
        responseBody
    );
    if (!success.success) {
        const failure = CardPointeCardSecureFailureResponseSchema.safeParse(responseBody);
        if (failure.success) {
            throw new Error(
                `CardPointe card tokenization failed: ${failure.data.message} ` +
                    `(error ${failure.data.errorcode}).`
            );
        }
        throw new Error(
            'CardPointe card tokenization response is missing expected fields.'
        );
    }
    return {
        result_type: CardPointeTokenizationResultType.CARDSECURE_BROWSER_TOKENIZATION,
        cardsecure_response: success.data,
    };
}
